"use client";
import emitter from "@packages/lib/eventEmitter/eventEmitter";
import { useRouter } from "next/navigation";
import { Suspense, useEffect, useState } from "react";
import SearchLabelsSkeleton from "@packages/shared-components/skeleton/search-result/search-labels-skeleton";
import { getScholarshipResultsURL } from "@packages/lib/utlils/helper-function";
import { isPGS } from "@packages/lib/utlils/commonFunction";

type LabelEntry = {
  urlKey: string[] | string;
  displayKey: string[] | string;
  filterName: string;
};

type LabelState = {
  selectedLabels: LabelEntry[];
  arrayLabels: LabelEntry[];
  nonArrayLabels: LabelEntry[];
};

const SearchLabelsContent = ({ selectedParams }: { selectedParams: any }) => {
  const router = useRouter();
  const [labelState, setLabelState] = useState<LabelState>({
    selectedLabels: [],
    arrayLabels: [],
    nonArrayLabels: [],
  });
  const [isLoading, setIsLoading] = useState(false);

  const formatDisplayName = (urlKey: string): string => {
    return urlKey.replace(/-/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
  };

  const setLabelsSelected = (
    selectedParams: any,
    displayNames: any,
  ): LabelState => {
    const labelMap = [
      { paramKey: "subject", labelKey: "subject", filterName: "subject" },
      { paramKey: "provider", labelKey: "provider", filterName: "provider" },
      {
        paramKey: "award_type",
        labelKey: "award_type",
        filterName: "award_type",
      },
      {
        paramKey: "award_coverage",
        labelKey: "award_coverage",
        filterName: "award_coverage",
      },
      {
        paramKey: "nationality",
        labelKey: "nationality",
        filterName: "nationality",
      },
      {
        paramKey: "studyLevel",
        labelKey: "studyLevel",
        filterName: "studyLevel",
      },
    ];

    const selectedLabels = labelMap.map(
      ({ paramKey, labelKey, filterName }) => ({
        urlKey: selectedParams?.[paramKey],
        displayKey: displayNames?.[labelKey] || selectedParams?.[paramKey],
        filterName,
      }),
    );

    const arrayLabels = selectedLabels.filter(
      (item) =>
        Array.isArray(item.urlKey) &&
        item.displayKey &&
        !(isPGS() && item.filterName === "studyLevel"),
    );

    const nonArrayLabels = selectedLabels.filter(
      (item) =>
        item.urlKey &&
        !Array.isArray(item.urlKey) &&
        !(isPGS() && item.filterName === "studyLevel"),
    );

    return { selectedLabels, arrayLabels, nonArrayLabels };
  };

  useEffect(() => {
    try {
      setIsLoading(true);
      const labels = setLabelsSelected(selectedParams, selectedParams);
      setLabelState(labels);
    } catch (error) {
      console.error("Error fetching display labels:", error);
    } finally {
      setIsLoading(false);
    }
  }, [selectedParams]);

  const openFilterFunction = () => {
    emitter.emit("isfilterOpen", "subject");
  };

  const removeFilter = (
    paramType: "array" | "non-array",
    paramKey: string,
    paramValue: string,
  ) => {
    const newSelectedParam = { ...selectedParams };

    if (paramType === "array") {
      const label = labelState.arrayLabels.find(
        (item) => item.filterName === paramKey,
      );
      if (label) {
        newSelectedParam[paramKey] = (label.urlKey as string[]).filter(
          (v) => v !== paramValue,
        );
      }
    } else if (paramType === "non-array") {
      if (paramKey === "provider") {
        delete newSelectedParam.provider;
      } else {
        newSelectedParam[paramKey] = null;
      }
    }

    const mappedParams = { ...newSelectedParam };
    // Map provider to university for filter component
    if (paramKey === "provider") {
      mappedParams.university = null;
    }
    if (mappedParams.award_type !== undefined) {
      mappedParams.awardType = mappedParams.award_type;
    }
    if (mappedParams.award_coverage !== undefined) {
      mappedParams.awardCoverage = mappedParams.award_coverage;
    }
    if (mappedParams.nationality !== undefined) {
      mappedParams.nationality = mappedParams.nationality;
    }
    if (mappedParams.subject !== undefined) {
      mappedParams.subject = mappedParams.subject;
    }

    // Map provider to university for filter component compatibility
    const eventParamKey = paramKey === "provider" ? "university" : paramKey;
    emitter.emit("filterRemoved", {
      paramKey: eventParamKey,
      paramValue,
      newParams: mappedParams,
    });

    // Trigger API call to refresh filter data and count
    emitter.emit("refreshScholarshipFilters", mappedParams);

    const labels = setLabelsSelected(newSelectedParam, newSelectedParam);
    setLabelState(labels);
    const newUrl = getScholarshipResultsURL(newSelectedParam);
    router.push(newUrl);
  };

  const shouldShowCloseIcon = (filterName: string, index: number) =>
    filterName !== "studyLevel";

  const LabelListElement = (
    urlText: string,
    displayText: string,
    filterName: string,
    index: number,
    arrayType: "array" | "non-array",
  ) => (
    <li
      key={`${filterName}-${urlText}`}
      className="bg-secondary-50 text-blue-500 whitespace-nowrap rounded-[4px] px-[10px] py-[3px] font-semibold x-small flex items-center gap-[2px]"
    >
      {displayText}
      {shouldShowCloseIcon(filterName, index) && (
        <svg
          onClick={() => removeFilter(arrayType, filterName, urlText)}
          className="cursor-pointer"
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M4 12L12 4M4 4L12 12"
            stroke="#3460DC"
            strokeWidth="1.13"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      )}
    </li>
  );

  return (
    <Suspense>
      {!isLoading ? (
        <section className="overflow-x-auto snap-x snap-mandatory bg-white px-[16px] py-[8px] md:px-[20px] xl:px-0 z-[3]">
          <div className="max-w-container mx-auto">
            <ul className="flex items-start gap-[8px] uppercase">
              {labelState?.arrayLabels?.map((item) =>
                (item.urlKey as string[]).map((urlText, index) => {
                  const displayText = formatDisplayName(urlText);
                  return LabelListElement(
                    urlText,
                    displayText,
                    item.filterName,
                    index,
                    "array",
                  );
                }),
              )}
              {labelState.nonArrayLabels.map((item, index) =>
                LabelListElement(
                  item.urlKey as string,
                  formatDisplayName(item.urlKey as string),
                  item.filterName,
                  index,
                  "non-array",
                ),
              )}
              <li
                onClick={openFilterFunction}
                className="bg-secondary-50 text-blue-500 whitespace-nowrap rounded-[4px] px-[10px] py-[2px] font-semibold x-small flex items-center gap-[2px] hidden lg:block"
              >
                <svg
                  className="cursor-pointer"
                  width="7"
                  height="20"
                  viewBox="0 0 7 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M6.55756 10.508H4.31356V12.788H2.68156V10.508H0.437563V8.96H2.68156V6.668H4.31356V8.96H6.55756V10.508Z"
                    fill="#3460DC"
                  />
                </svg>
              </li>
            </ul>
          </div>
        </section>
      ) : (
        <SearchLabelsSkeleton />
      )}
    </Suspense>
  );
};

const SearchLabels = ({ searchPayLoad }: any) => {
  return (
    <Suspense fallback={<SearchLabelsSkeleton />}>
      <SearchLabelsContent selectedParams={searchPayLoad} />
    </Suspense>
  );
};

export default SearchLabels;
